import { signal } from '@preact/signals';
import { api, authState } from '../state/auth.js';

// The afdelingen of an omnivereniging, with per section, commission and category the
// department it is in (GET /api/settings/departments). Loaded once per user and club;
// the structure page reloads it after a change.
export const departments = signal(null);
let pending = null;

const currentKey = () => `${authState.value.userId}:${authState.value.activeClubId}`;

export function loadDepartments({ force = false } = {}) {
  const key = currentKey();
  if (!force && departments.value?.key === key) return Promise.resolve(departments.value);
  if (!force && pending?.key === key) return pending.promise;
  const promise = api('/api/settings/departments')
    // Unknown (request failed): no labels, nothing else depends on it
    .then(data => ({ ...data, key }), () => ({ key, failed: true, departments: [], sections: {}, commissions: {}, categories: {} }))
    .then(value => { if (pending?.key === key) departments.value = value; return value; });
  pending = { key, promise };
  return promise;
}

// Only the departments of the club that is active now
function current() {
  return departments.value?.key === currentKey() ? departments.value : null;
}

const TABLE = { section: 'sections', commission: 'commissions', category: 'categories' };

// The departments of a list of targets ({ type: 'section' | 'commission' | 'category', id }),
// each once, in the club's order. Empty when the club has fewer than two: with one,
// everything is that one.
export function departmentsOf(targets) {
  const value = current();
  if (!value || value.departments.length < 2 || !targets?.length) return [];
  const ids = new Set(targets.map(t => value[TABLE[t.type]]?.[t.id]).filter(Boolean));
  return value.departments.filter(d => ids.has(d.id));
}

// The departments to choose from (roles, starting a process): only when the club has
// two or more; with one there is nothing to choose
export function departmentChoices() {
  const value = current();
  return value && value.departments.length >= 2 ? value.departments : [];
}

// All the club's departments once they are in; null while loading or when the request
// failed (then "none" would be a wrong answer)
export function loadedDepartments() {
  const value = current();
  return value && !value.failed ? value.departments : null;
}

export function departmentById(id) {
  return current()?.departments.find(d => d.id === id) || null;
}
